'use client';

import { useState } from 'react';
import { motion } from 'framer-motion';
import Joyride from 'react-joyride';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getTourStyles } from '@/components/tour/tourStyles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';

import { Card, CardContent } from '@/components/ui/card';
import { GLASS_CARD } from '@/components/ui/glass';
import { CheckCircle2, Search, X } from 'lucide-react';
import Pagination from '@/components/pages/shared/Pagination';
import {
  DLG_BTN_SECONDARY,
  DLG_BTN_DANGER,
} from '@/components/ui/dialogButtons';

import getAbsencesTourSteps from '@/components/tour/company/AbsencesTourSteps';
import { useAusenciasPorEmpresa } from '@/components/hooks/useAusencias';
import { useDiasFestivos } from '@/components/hooks/useDiasFestivos';
import { useDiasLaborablesPorEmpresa } from '@/components/hooks/useHorarios';
import { DIAS_SEMANA } from '@/components/constants/dias';
import { usePageHelp, useTourVisibility } from '@/components/hooks/usePageHelp';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export default function Absences() {
  const { t } = useTranslation();
  const isMobile = window.innerWidth < 640;
  const empresaId = localStorage.getItem('id_empresa') || '';
  const userId = localStorage.getItem('id_usuario') || null;

  const { ausencias, updateEstadoAusencia } = useAusenciasPorEmpresa(empresaId);
  const diasFestivos = useDiasFestivos(empresaId);
  const diasLaborables = useDiasLaborablesPorEmpresa(empresaId);

  const [filtroTexto, setFiltroTexto] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [runTour, setRunTour] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [rechazoMotivo, setRechazoMotivo] = useState('');
  const [rechazoId, setRechazoId] = useState<string | null>(null);

  const pendientes = ausencias.filter((a) => a.estado_ausencia === 'pendiente');

  const steps = getAbsencesTourSteps(isMobile, pendientes.length > 0);

  // El botón global de ayuda (arriba a la derecha) lanza el tour de esta página.
  usePageHelp(() => setRunTour(true));
  useTourVisibility(runTour);

  /* Las fechas vienen como 'YYYY-MM-DD'. new Date(s) las interpreta en UTC,
     lo que desplaza el día al formatear en hora local, así que las
     construimos a mano en local. */
  const parseFechaLocal = (s: string) => {
    const [y, m, d] = s.split('T')[0].split('-').map(Number);
    return new Date(y, m - 1, d);
  };

  const formatearFecha = (fecha: string) => {
    const [y, m, d] = fecha.split('T')[0].split('-').map(Number);
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
  };

  /* Un día cuenta como ausencia si el trabajador lo tiene en su horario
     y no es festivo. Si no hay horario configurado, se asume L-V. */
  const calcularDiasAusencia = (
    inicio: string,
    fin: string,
    idUsuario: string
  ) => {
    const diasTrabajador = diasLaborables[idUsuario];
    let count = 0;
    const cur = parseFechaLocal(inicio);
    const end = parseFechaLocal(fin);

    while (cur <= end) {
      const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(
        2,
        '0'
      )}-${String(cur.getDate()).padStart(2, '0')}`;

      // getDay(): 0 = domingo; DIAS_SEMANA empieza en lunes
      const nombreDia = DIAS_SEMANA[(cur.getDay() + 6) % 7];

      const trabaja = diasTrabajador
        ? diasTrabajador.has(nombreDia)
        : cur.getDay() !== 0 && cur.getDay() !== 6;

      if (!diasFestivos.includes(iso) && trabaja) count++;
      cur.setDate(cur.getDate() + 1);
    }
    return count;
  };

  const handleAbrirRechazo = (id: string) => {
    setRechazoId(id);
    setRechazoMotivo('');
    setDialogOpen(true);
  };

  const handleConfirmarRechazo = async () => {
    if (rechazoId) {
      await handleActualizarEstado(
        rechazoId,
        'rechazado',
        rechazoMotivo.trim()
      );
    }
    setDialogOpen(false);
  };

  const handleActualizarEstado = async (
    id_ausencia: string,
    estado: 'pendiente' | 'aprobado' | 'rechazado',
    motivo?: string
  ) => {
    const resultado = await updateEstadoAusencia(
      id_ausencia,
      estado,
      motivo,
      userId || undefined
    );

    if (!resultado.ok) return;

    const ausencia = ausencias.find((a) => a.id_ausencia === id_ausencia);
    if (!ausencia) return;

    try {
    } catch (err) {
      console.error('Error enviando correo de notificación:', err);
      toast.error(t('absences.errorEmail'));
    }
  };

  const historico = ausencias.filter((a) => a.estado_ausencia !== 'pendiente');

  const filtradoHistorico = historico
    .filter((a) => {
      const texto = filtroTexto.toLowerCase();
      return (
        a.nombre_completo.toLowerCase().includes(texto) ||
        a.email.toLowerCase().includes(texto) ||
        a.tipo_ausencia.toLowerCase().includes(texto)
      );
    })
    .sort(
      (a, b) =>
        new Date(b.fecha_inicio).getTime() - new Date(a.fecha_inicio).getTime()
    );

  const totalPages = Math.ceil(filtradoHistorico.length / pageSize) || 1;
  const currentData = filtradoHistorico.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const changePageSize = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  return (
    <motion.div
      className="space-y-8 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header — el botón de ayuda vive ahora arriba a la derecha, fijo
          y global (HelpButton), lanzado desde usePageHelp. */}
      <motion.div variants={itemVariants}>
        <div className="tour-title flex items-center text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            {t('absences.title')}
          </span>
        </div>
      </motion.div>

      <Joyride
        steps={steps}
        run={runTour}
        continuous
        showSkipButton
        spotlightClicks
        locale={{
          back: t('profiles.tour.back'),
          close: t('profiles.tour.close'),
          last: t('profiles.tour.last'),
          next: t('profiles.tour.next'),
          skip: t('profiles.tour.skip'),
        }}
        callback={({ status }) =>
          status === 'finished' || status === 'skipped'
            ? setRunTour(false)
            : null
        }
        styles={getTourStyles(isMobile)}
      />

      {/* Pendientes */}
      <motion.section
        variants={itemVariants}
        className="tour-pendientes-section"
      >
        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-4">
          {t('absences.pendingTitle')}
        </h2>

        {pendientes.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-12 text-center flex flex-col items-center">
              <CheckCircle2 className="w-10 h-10 text-green-500 dark:text-green-400 mb-4" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('absences.noPending')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {pendientes.map((a, idx) => (
              <motion.div
                key={a.id_ausencia}
                variants={itemVariants}
                className={`rounded-xl p-4 ${GLASS_CARD} flex flex-col md:flex-row md:justify-between md:items-center`}
              >
                <div className="space-y-1">
                  <p className="font-medium text-gray-800 dark:text-white">
                    {a.nombre_completo}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {a.email}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {a.tipo_ausencia} | {formatearFecha(a.fecha_inicio)} —{' '}
                    {formatearFecha(a.fecha_fin)}
                  </p>
                </div>

                <div className="flex gap-2 mt-4 md:mt-0">
                  <Button
                    size="sm"
                    className={`rounded-lg text-white font-medium shadow-md bg-emerald-600 hover:bg-emerald-700 px-4 py-1 transition-colors ${
                      idx === 0 ? 'tour-aprobar-btn' : ''
                    }`}
                    onClick={() =>
                      handleActualizarEstado(a.id_ausencia, 'aprobado')
                    }
                  >
                    {t('absences.approve')}
                  </Button>

                  <Button
                    size="sm"
                    className={`rounded-lg text-white font-medium shadow-md bg-red-600 hover:bg-red-700 px-4 py-1 transition-colors ${
                      idx === 0 ? 'tour-rechazar-btn' : ''
                    }`}
                    onClick={() => handleAbrirRechazo(a.id_ausencia)}
                  >
                    {t('absences.reject')}
                  </Button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </motion.section>

      <motion.hr
        variants={itemVariants}
        className="my-8 border-t border-gray-200 dark:border-white/10"
      />

      {/* Histórico */}
      <motion.section
        variants={itemVariants}
        className="tour-historico-section space-y-4"
      >
        <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-2">
          {t('absences.historyTitle')}
        </h2>

        <div className="relative w-full sm:w-1/2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <Input
            type="text"
            placeholder={t('absences.filterPlaceholder')}
            className="tour-historico-input pl-9 w-full dark:bg-white/5 dark:border-white/10 dark:text-white"
            value={filtroTexto}
            onChange={(e) => {
              setFiltroTexto(e.target.value);
              setPage(1);
            }}
          />
        </div>

        {currentData.length === 0 ? (
          <Card className={`rounded-2xl ${GLASS_CARD}`}>
            <CardContent className="py-16 text-center">
              <Search className="w-10 h-10 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
              <p className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                {t('absences.noResults')}
              </p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {t('absences.noResultsText')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Vista móvil - Tarjetas */}
            <div className="block lg:hidden space-y-4">
              {currentData.map((a, idx) => (
                <motion.div
                  key={a.id_ausencia}
                  variants={itemVariants}
                  className={`rounded-xl p-4 ${GLASS_CARD}`}
                >
                  <div className="space-y-3">
                    <div>
                      <p className="font-medium text-gray-800 dark:text-white">
                        {a.nombre_completo}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {a.email}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">
                          {t('absences.cardType')}
                        </span>
                        <p className="font-medium text-gray-800 dark:text-white">
                          {a.tipo_ausencia.charAt(0).toUpperCase() +
                            a.tipo_ausencia.slice(1)}
                        </p>
                      </div>

                      <div>
                        <span className="text-gray-500 dark:text-gray-400 text-xs">
                          {t('absences.cardDays')}
                        </span>
                        <p className="font-medium text-gray-800 dark:text-white">
                          {calcularDiasAusencia(
                            a.fecha_inicio,
                            a.fecha_fin,
                            a.id_usuario
                          )}
                        </p>
                      </div>

                      <div className="col-span-2">
                        <span className="text-gray-500 dark:text-gray-400 text-xs">
                          {t('absences.cardDates')}
                        </span>
                        <p className="font-medium text-gray-800 dark:text-white text-xs">
                          {formatearFecha(a.fecha_inicio)} —{' '}
                          {formatearFecha(a.fecha_fin)}
                        </p>
                      </div>
                    </div>

                    <div className="flex justify-between items-center pt-2 border-t border-gray-100 dark:border-white/10">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          a.estado_ausencia === 'aprobado'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                            : a.estado_ausencia === 'rechazado'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                            : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                        }`}
                      >
                        {a.estado_ausencia.charAt(0).toUpperCase() +
                          a.estado_ausencia.slice(1)}
                      </span>

                      <Button
                        size="sm"
                        variant="outline"
                        className={`rounded-lg px-3 h-8 text-yellow-700 dark:text-yellow-400 border-yellow-600 dark:border-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 ${
                          idx === 0 ? 'tour-reabrir-btn' : ''
                        }`}
                        onClick={() =>
                          handleActualizarEstado(a.id_ausencia, 'pendiente')
                        }
                      >
                        {t('absences.reopen')}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Vista desktop - TABLA */}
            <Card className={`hidden lg:block rounded-2xl ${GLASS_CARD}`}>
              <CardContent className="overflow-x-auto p-0">
                <table className="min-w-full table-auto text-sm">
                  <thead className="tour-table-head bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-gray-300 uppercase text-xs sticky top-0 z-10">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        {t('absences.colName')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('absences.colEmail')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('absences.colType')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('absences.colDates')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('absences.colDays')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('absences.colStatus')}
                      </th>
                      <th className="px-4 py-3 text-left">
                        {t('absences.colActions')}
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                    {currentData.map((a, idx) => (
                      <motion.tr
                        key={a.id_ausencia}
                        variants={itemVariants}
                        className={
                          idx % 2 === 0
                            ? 'bg-white dark:bg-transparent'
                            : 'bg-gray-50 dark:bg-white/[0.03]'
                        }
                      >
                        <td className="px-4 py-3 font-medium text-gray-800 dark:text-white">
                          {a.nombre_completo}
                        </td>

                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {a.email}
                        </td>

                        <td className="px-4 py-3 text-gray-800 dark:text-white">
                          {a.tipo_ausencia.charAt(0).toUpperCase() +
                            a.tipo_ausencia.slice(1)}
                        </td>

                        <td className="px-4 py-3 whitespace-nowrap text-gray-800 dark:text-white">
                          {formatearFecha(a.fecha_inicio)} —{' '}
                          {formatearFecha(a.fecha_fin)}
                        </td>

                        <td className="px-4 py-3 whitespace-nowrap text-gray-800 dark:text-white">
                          {calcularDiasAusencia(
                            a.fecha_inicio,
                            a.fecha_fin,
                            a.id_usuario
                          )}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              a.estado_ausencia === 'aprobado'
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                : a.estado_ausencia === 'rechazado'
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                                : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
                            }`}
                          >
                            {a.estado_ausencia.charAt(0).toUpperCase() +
                              a.estado_ausencia.slice(1)}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <Button
                            size="sm"
                            variant="outline"
                            className={`rounded-lg px-3 h-8 text-yellow-700 dark:text-yellow-400 border-yellow-600 dark:border-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 ${
                              idx === 0 ? 'tour-reabrir-btn' : ''
                            }`}
                            onClick={() =>
                              handleActualizarEstado(a.id_ausencia, 'pendiente')
                            }
                          >
                            {t('absences.reopen')}
                          </Button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            {/* Paginación */}
            <Pagination
              page={page}
              totalPages={totalPages}
              totalItems={filtradoHistorico.length}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={changePageSize}
              itemLabel="ausencias"
              className="tour-pagination"
            />
          </>
        )}
      </motion.section>

      {/* Diálogo de rechazo (bottom sheet) */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="
            z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
            [&>button:last-child]:hidden
            flex flex-col
            !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
            w-full max-w-none rounded-t-2xl rounded-b-none
            data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
            sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
            sm:w-[95%] sm:max-w-md sm:mx-auto sm:h-auto sm:rounded-xl sm:shadow-xl
          "
        >
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative">
            <DialogHeader>
              <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10">
                {t('absences.rejectDialogTitle')}
              </DialogTitle>
            </DialogHeader>
            <button
              onClick={() => setDialogOpen(false)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-4 sm:px-6 pb-5">
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {t('absences.rejectDialogText')}
              </p>
              <textarea
                className="w-full border border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white px-3 py-2 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#103254] focus:border-[#103254]"
                rows={4}
                placeholder={t('absences.rejectPlaceholder')}
                value={rechazoMotivo}
                onChange={(e) => setRechazoMotivo(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
            >
              {t('absences.cancel')}
            </Button>
            <Button
              onClick={handleConfirmarRechazo}
              className={`w-full sm:flex-1 ${DLG_BTN_DANGER}`}
            >
              {t('absences.reject')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}